import { mijnBeurt, regelsVoor, tekendeZelfAl, volgendeStap } from './keten'
import type { Expense } from './types'

/* ------------------------------------------------------------------ *
 *  Wat er nog moet gebeuren met een binnengekomen factuur
 *
 *  Casper vroeg om een aparte werklijst met statussen: "wat is er binnen,
 *  wat is er gelezen, wat is er gecontroleerd, wat is er geboekt, en wat is
 *  er vastgelopen."
 *
 *  Er komt geen nieuwe tabel aan te pas. Alles wat daarvoor nodig is staat al
 *  op de kostenpost: lees_status (0049), status, exact_id en exact_fout
 *  (0053), de eerste handtekening (0060). Wat ontbrak was niet gegevens maar
 *  een antwoord op de vraag "en wat nu?" -- en die vraag stond nergens in een
 *  functie, dus stond hij in het hoofd van degene die de lijst doorliep.
 *
 *  Hier staat hij wel, als pure functie, zodat de zelftest hem kan narekenen
 *  en het scherm hem niet nog eens hoeft uit te schrijven.
 * ------------------------------------------------------------------ */

/**
 * De standen, in de volgorde waarin ze langskomen.
 *
 * Ze sluiten elkaar uit: een bon heeft er precies een. Dat is met opzet --
 * een bon die in twee kolommen staat is een bon die twee mensen oppakken of
 * geen van beiden.
 */
export type Verwerkstand =
  | 'lezen'
  | 'vastgelopen'
  | 'aanvullen'
  | 'valideren'
  | 'akkoord'
  | 'tweede'
  | 'afronden'
  | 'boeken'
  | 'betalen'
  | 'geweigerd'
  | 'klaar'
  | 'afgekeurd'

export interface Standinfo {
  sleutel: Verwerkstand
  label: string
  /** Een zin die zegt wat er van jou wordt verwacht, niet wat de stand heet. */
  uitleg: string
  /** Ligt het werk hier bij ons, of wachten we op iets anders. */
  actie: boolean
  /** Hier is iets misgegaan; die krijgen voorrang. */
  stuk?: boolean
  /** Hier is niets meer te doen; blijft buiten de werklijst. */
  afgerond?: boolean
}

export const STANDEN: Record<Verwerkstand, Standinfo> = {
  vastgelopen: {
    sleutel: 'vastgelopen',
    label: 'Vastgelopen',
    uitleg: 'Het lezen is niet gelukt. Probeer het opnieuw of vul het met de hand in.',
    actie: true,
    stuk: true,
  },
  geweigerd: {
    sleutel: 'geweigerd',
    label: 'Exact weigert',
    uitleg: 'De boeking is door Exact teruggestuurd. Er staat bij waarom.',
    actie: true,
    stuk: true,
  },
  lezen: {
    sleutel: 'lezen',
    label: 'Wordt gelezen',
    uitleg: 'De factuur staat in de wachtrij bij de AI-assistent. Hier hoef je niets te doen.',
    actie: false,
  },
  aanvullen: {
    sleutel: 'aanvullen',
    label: 'Aanvullen',
    uitleg: 'Er is gelezen, maar er ontbreekt iets waar je niet op kunt tekenen.',
    actie: true,
  },
  valideren: {
    sleutel: 'valideren',
    label: 'Te valideren',
    uitleg: 'Compleet gelezen. De administratie kijkt na of het stuk klopt.',
    actie: true,
  },
  akkoord: {
    sleutel: 'akkoord',
    label: 'Wacht op akkoord',
    uitleg: 'Nagekeken. Iemand moet er ja of nee op zeggen.',
    actie: true,
  },
  tweede: {
    sleutel: 'tweede',
    label: 'Tweede handtekening',
    uitleg: 'De eerste staat er. De tweede moet iemand anders zijn.',
    actie: true,
  },
  /*
   * De afronding (0173).
   *
   * Een eigen stand en geen randje bij "tweede handtekening": het is werk
   * voor weer iemand ANDERS, en het is de stap waarna het geld daadwerkelijk
   * in de boekhouding belandt. Zou hij bij 'tweede' blijven zitten, dan staat
   * er in de werklijst één stapel waar twee verschillende mensen in moeten
   * kijken -- en dan kijkt er geen.
   */
  afronden: {
    sleutel: 'afronden',
    label: 'Afronding',
    uitleg: 'Twee handtekeningen staan er. Na de afronding gaat hij naar Exact.',
    actie: true,
  },
  boeken: {
    sleutel: 'boeken',
    label: 'Naar Exact',
    uitleg: 'Goedgekeurd en klaar om geboekt te worden.',
    actie: true,
  },
  /*
   * Betalen is werk, en toch staat het niet in deze lijst.
   *
   * Het hoort bij de stroom (zie stroom.ts) en bij het scherm Betalen, waar
   * je facturen in een betaalopdracht zet en er een SEPA-bestand van maakt.
   * Dat is een andere handeling dan "kijk hiernaar en zeg er ja of nee op",
   * en het gaat om honderden regels tegelijk. Zou hij in VOLGORDE staan, dan
   * groeit de werklijst met alles wat op de bank wacht en is de handvol
   * facturen waar een mens werkelijk naar moet kijken er niet meer in terug
   * te vinden.
   *
   * Vandaar: een echte stand, geteld in de stroom, maar buiten de werklijst.
   * actie blijft true omdat het werkelijk werk is; telWerk() kijkt daarom
   * niet alleen naar actie maar ook naar VOLGORDE.
   */
  betalen: {
    sleutel: 'betalen',
    label: 'Te betalen',
    uitleg: 'Geboekt in Exact en nog niet betaald. Gaat via een betaalopdracht.',
    actie: true,
  },
  klaar: {
    sleutel: 'klaar',
    label: 'Betaald',
    uitleg: 'Geboekt en betaald. Hier is niets meer te doen.',
    actie: false,
    afgerond: true,
  },
  afgekeurd: {
    sleutel: 'afgekeurd',
    label: 'Afgekeurd',
    uitleg: 'Iemand heeft deze afgewezen.',
    actie: false,
    afgerond: true,
  },
}

/**
 * Hoe lang een lezing mag duren voor we hem als vastgelopen beschouwen.
 *
 * Tien minuten, dezelfde grens als de serverkant hanteert voor lees_geclaimd_at.
 * Zonder deze grens blijft een bon waarvan de leescomputer halverwege uitviel
 * eeuwig op 'bezig' staan -- zichtbaar in geen enkele lijst, want technisch is
 * er iemand mee bezig.
 */
export const LEZEN_DUURT_HOOGSTENS = 10 * 60 * 1000

/**
 * In welke stand staat deze bon?
 *
 * De volgorde van de vragen is de hele functie. Eerst wat kapot is, dan wat
 * af is, dan wat er nog moet gebeuren -- want een bon die goedgekeurd is en
 * waarvan Exact een fout teruggaf, hoort bij "er is iets mis" en niet bij
 * "moet nog geboekt worden".
 */
export function standVan(bon: Expense, nu: number = Date.now()): Verwerkstand {
  if (bon.status === 'afgekeurd') return 'afgekeurd'

  /* Exact eerst: een fout hier is de enige stand waar niemand vanzelf
     uitkomt. Blijft hij tussen 'goedgekeurd' hangen, dan ziet het eruit als
     werk dat nog moet gebeuren terwijl het al drie keer is geprobeerd. */
  if (bon.exactFout) return 'geweigerd'
  /*
   * Geboekt is niet hetzelfde als afgehandeld.
   *
   * Hier stond 'klaar' zodra er een boekingsnummer was, en daarmee viel de
   * hele betaalkant buiten beeld: honderden facturen die in Exact stonden en
   * nog betaald moesten worden, telden als afgerond. In Blue10 is dat de
   * grootste stapel van allemaal, en bij ons was hij onzichtbaar.
   */
  if (bon.exactId) return bon.betaaldAt ? 'klaar' : 'betalen'

  if (bon.status === 'goedgekeurd') return 'boeken'
  if (bon.status === 'tweede_akkoord') return 'afronden'
  if (bon.status === 'eerste_akkoord') return 'tweede'
  /* Gevalideerd en nog niet getekend: dan wacht hij op de eerste
     handtekening. Dat is dezelfde stand als 'akkoord' hieronder, want wat er
     ontbreekt is al nagekeken toen er gevalideerd werd. */
  if (bon.status === 'gevalideerd') return 'akkoord'

  /* Het lezen. 'mislukt' is duidelijk; 'bezig' dat te lang duurt is dat niet,
     en juist daar blijft werk liggen. */
  if (bon.leesStatus === 'mislukt') return 'vastgelopen'
  if (bon.leesStatus === 'bezig'
      && bon.leesGeclaimdAt != null
      && nu - bon.leesGeclaimdAt > LEZEN_DUURT_HOOGSTENS) {
    return 'vastgelopen'
  }
  if (bon.leesStatus === 'wacht' || bon.leesStatus === 'bezig') return 'lezen'

  /*
   * En anders staat hij nog open: eerst nakijken (0190).
   *
   * Ontbreekt er iets aan het stuk, dan is dat het eerste werk -- daarna
   * valideren. Hier stond 'akkoord' voor allebei, en dat klopte toen
   * valideren en tekenen dezelfde knop waren.
   */
  return ontbreekt(bon).length > 0 ? 'aanvullen' : 'valideren'
}

/**
 * Wat er aan deze bon ontbreekt om er ja op te kunnen zeggen.
 *
 * Leesbare zinnen en geen veldnamen: dit komt op het scherm te staan naast de
 * bon, en "amountExcl" zegt de administratie niets.
 *
 * De twijfel van de AI-assistent telt mee. Casper: "Ga niet zomaar gokken." Een
 * model dat zelf zegt dat het ergens niet uit kwam, hoort niet stil in de rij
 * "wacht op akkoord" te belanden alsof er niets aan de hand is.
 */
export function ontbreekt(bon: Expense): string[] {
  const uit: string[] = []

  /*
   * Geen bedrag is NUL, niet "onder nul".
   *
   * Hier stond `> 0`, en daarmee was een creditnota een bon zonder bedrag.
   * Dat is precies verkeerd om: op een creditnota staat het bedrag er juist
   * wél, het gaat alleen de andere kant op. De bon kwam zo in "Aanvullen" te
   * staan met de mededeling "Er staat geen bedrag op" naast een stuk waar
   * -1.250,00 op gedrukt stond, en er was niets aan te vullen -- het stond er
   * al. Hij bleef dus staan, elke dag opnieuw.
   *
   * Nul blijft wel ontbreken. Dat is de toestand van een bon die de post net
   * heeft aangemaakt en waar nog niemand en niets in heeft gezeten.
   */
  if (bon.amountExcl === 0 || !Number.isFinite(bon.amountExcl)) {
    uit.push('Er staat geen bedrag op.')
  }
  if (!bon.supplier.trim()) uit.push('De leverancier is niet ingevuld.')
  if (!bon.date) uit.push('De factuurdatum ontbreekt.')

  /*
   * De vennootschap (0079).
   *
   * Een bv die alleen maar "lijkt te kloppen" hoort niet stil door te gaan.
   * Hij bepaalt in welke jaarrekening de post landt, en een fout daarin komt
   * pas bij de accountant boven -- als niemand meer weet waar die factuur
   * vandaan kwam. Dus staat hij hier, tussen de dingen waarop je niet blind
   * ja zegt.
   *
   * Alleen een vermoeden, niet een lege bv: leeg betekent dat hij uit de
   * vestiging volgt, en dat is hoe het tot 0079 altijd ging.
   */
  if (bon.administratieBron === 'vermoeden') {
    uit.push(bon.geadresseerde
      ? `De factuur is gericht aan "${bon.geadresseerde}". Welke onderneming dat is, `
        + 'is geraden op de naam -- kijk het na.'
      : 'Welke onderneming dit wordt, is geraden op de naam -- kijk het na.')
  }

  const lezing = bon.gelezen
  if (lezing) {
    for (const zin of lezing.twijfel ?? []) uit.push(zin)
    /*
     * Een verkoopfactuur tussen de inkoop is geen ontbrekend veld maar wel
     * iets waar niemand blind ja op moet zeggen: dan boek je je eigen omzet
     * als kosten.
     */
    if (lezing.richting === 'verkoop') {
      uit.push('Dit lijkt een factuur van onszelf, geen inkoopfactuur.')
    }
    if (lezing.gemarkeerd) {
      uit.push(`De bijlage is tegengehouden in de postbus: ${lezing.gemarkeerd}`)
    }
  }

  return uit
}

/** Is er iets om opnieuw te laten lezen? Zonder bijlage valt er niets te lezen. */
export function isTeLezen(bon: Expense): boolean {
  return !!bon.attachmentPath || !!bon.mailboxId
}

export interface Vak {
  stand: Standinfo
  bonnen: Expense[]
}

/**
 * De bonnen over de vakken verdelen, in de volgorde waarin je ze wil zien.
 *
 * Kapot bovenaan, dan wat op jou wacht, dan wat vanzelf verdergaat. Wat af is
 * doet niet mee: een werklijst waar het afgeronde werk in blijft staan, wordt
 * elke maand langer en elke maand minder gelezen.
 */
export const VOLGORDE: Verwerkstand[] = [
  'vastgelopen', 'geweigerd', 'aanvullen', 'valideren', 'akkoord', 'tweede', 'afronden',
  'boeken', 'lezen',
]

export function verdeel(bonnen: Expense[], nu: number = Date.now()): Vak[] {
  const per = new Map<Verwerkstand, Expense[]>()
  for (const bon of bonnen) {
    const stand = standVan(bon, nu)
    if (STANDEN[stand].afgerond) continue
    const rij = per.get(stand)
    if (rij) rij.push(bon)
    else per.set(stand, [bon])
  }

  return VOLGORDE
    .filter((s) => (per.get(s)?.length ?? 0) > 0)
    .map((s) => ({
      stand: STANDEN[s],
      /* Oudste bovenaan: wat het langst ligt, ligt er niet voor niets. */
      bonnen: (per.get(s) ?? []).sort((a, b) => a.date - b.date),
    }))
}

/**
 * Hoeveel bonnen er op iemand wachten. Dat is het getal voor de badge.
 *
 * Op VOLGORDE en niet alleen op `actie`, en dat verschil is er pas sinds
 * 'betalen' bestaat. Die stand is werk -- er moet geld overgemaakt worden --
 * maar hij hoort bij het scherm Betalen en niet bij deze lijst. Zonder deze
 * grens sprong de badge van "Te verwerken" van een handvol naar honderden, en
 * dan zegt hij niets meer.
 *
 * Vandaag telt dit precies hetzelfde als vroeger: elke stand in VOLGORDE heeft
 * actie. Het staat er voor de volgende die een stand toevoegt.
 */
export function telWerk(bonnen: Expense[], nu: number = Date.now()): number {
  return bonnen.filter((b) => {
    const stand = standVan(b, nu)
    return STANDEN[stand].actie && VOLGORDE.includes(stand)
  }).length
}

/** En hoeveel daarvan stuk zijn. Die krijgen een andere kleur. */
export function telStuk(bonnen: Expense[], nu: number = Date.now()): number {
  return bonnen.filter((b) => STANDEN[standVan(b, nu)].stuk).length
}

/* ------------------------------------------------------------------ *
 *  Wat er op DIT MOMENT bij jou ligt
 *
 *  Casper: "Daarnaast moet je te verwerken persoonlijk maken ... bij te
 *  verwerken moeten enkel de facturen komen die op dat moment op de persoon
 *  staan, zodat je niet schrikt van dat nummer, en in 1x door kan werken."
 *
 *  De werklijst telde tweehonderdtwee, en op de tegel stond erbij "wachten
 *  op jou". Dat was niet waar: het overgrote deel wachtte op de eerste
 *  handtekening van iemand anders. Een getal dat te groot is om aan te
 *  beginnen is erger dan geen getal -- je begint er niet aan.
 *
 *  Wie aan de beurt is, staat al in lib/keten.ts en wordt hier niet
 *  overgedaan. Dat is geen vormkwestie: twee antwoorden op "van wie is deze
 *  factuur" is precies hoe een stapel bij de verkeerde persoon komt te
 *  liggen, en het is de reden dat keten.ts bestaat.
 * ------------------------------------------------------------------ */

/**
 * Ligt deze factuur nu bij mij?
 *
 * Twee gevallen, en ze zijn allebei nodig:
 *
 *   er komt nog een handtekening   dan beslist mijnBeurt() in lib/keten.ts:
 *                                  sta ik in de groep (of is die leeg, en
 *                                  mag dus iedereen die over kosten
 *                                  beslist), en tekende ik niet zelf al
 *   er komt er geen meer           getekend en nog niet in Exact -- 'Naar
 *                                  Exact' en 'Exact weigert'. Daar hangt
 *                                  geen naam aan; dat is werk van de
 *                                  administratie samen. Wat van iedereen is
 *                                  hoort op ieders lijst, want anders staat
 *                                  het op niemands lijst
 *
 * Alles wat afgerond is (betaald, afgekeurd) en alles wat buiten de
 * werklijst valt (betalen gaat via een betaalopdracht) valt in dat tweede
 * geval vanzelf af: VOLGORDE en `actie` bepalen dat al, en die regel staat
 * hier niet voor de tweede keer.
 */
export function vanMij(
  bon: Expense,
  wie: string,
  instellingen: Record<string, string>,
  nu: number = Date.now(),
): boolean {
  /*
   * Alleen wat op jouw NAAM staat (20 okt 2026), en niet meer "wat van
   * iedereen is". Casper: "echt bij je eigen werk terecht komt". Valideren,
   * Exact en een stap zonder naam hebben elk een eigen bak; zie bakkenVan().
   */
  if (!telt(bon, nu)) return false
  const stand = standVan(bon, nu)
  if (stand === 'boeken' || stand === 'geweigerd') return false
  const stap = volgendeStap(bon, regelsVoor(instellingen, bon.amountExcl))
  return mijnBeurt(stap, wie)
}

const telt = (b: Expense, nu: number) => {
  const s = standVan(b, nu)
  return STANDEN[s].actie && VOLGORDE.includes(s)
}

/** De vier bakken van "Te verwerken", op de computer en op de telefoon. */
export interface Bakken {
  /** De stap die nu aan de beurt is heeft jouw naam, en je tekende niet zelf al. */
  mij: Expense[]
  /** De gedeelde stapel van de administratie (0190: valideren heeft geen naam). */
  valideren: Expense[]
  /** Goedgekeurd en nog niet geboekt, of door Exact geweigerd. */
  exact: Expense[]
  /** Een stap waar niemand bij staat: niemand kan tekenen tot de route er is. */
  zonderNaam: Expense[]
}

/**
 * Te verwerken, in vier bakken.
 *
 * Eerst gebouwd voor TW10 op de telefoon (Casper: "enkel de lijst krijg waar
 * ik ook echt verantwoordelijk voor ben"), sinds 20 oktober 2026 ook voor de
 * computer: "Wat ik nog mis, is dat je echt bij je eigen werk terecht komt."
 * Eén regel voor allebei, zodat dezelfde factuur niet op de telefoon bij jou
 * ligt en op de computer bij een ander.
 *
 * `mij` is precies mijnWerk(); de rest valt daar buiten maar mag niet
 * verdwijnen -- vandaar hun eigen bak.
 */
export function bakkenVan(
  bonnen: Expense[],
  wie: string,
  instellingen: Record<string, string>,
  magValideren: boolean,
  nu: number = Date.now(),
): Bakken {
  const uit: Bakken = { mij: [], valideren: [], exact: [], zonderNaam: [] }
  for (const b of bonnen) {
    if (!telt(b, nu)) continue
    const stand = standVan(b, nu)
    if (stand === 'boeken' || stand === 'geweigerd') { uit.exact.push(b); continue }
    const stap = volgendeStap(b, regelsVoor(instellingen, b.amountExcl))
    if (!stap) continue
    if (stap.stap === 'valideren') {
      if (magValideren) uit.valideren.push(b)
      continue
    }
    if (stap.groep.length === 0) { uit.zonderNaam.push(b); continue }
    if (stap.groep.includes(wie) && !tekendeZelfAl(stap, wie)) uit.mij.push(b)
  }
  return uit
}

/**
 * De stapel van één persoon.
 *
 * Geeft een LIJST terug en geen getal, met opzet: het scherm moet dezelfde
 * facturen tonen als de tegel telt. Zou hier een teller staan en daar een
 * filter, dan lopen ze bij de eerstvolgende wijziging uit elkaar -- en dan
 * klik je op een tegel die zeven zegt en zie je er vier.
 *
 * Tellen doe je er daarna mee: telWerk(mijnWerk(...)) is het getal op de
 * tegel, telStuk(mijnWerk(...)) de rode variant ervan.
 */
export function mijnWerk(
  bonnen: Expense[],
  wie: string,
  instellingen: Record<string, string>,
  nu: number = Date.now(),
): Expense[] {
  /*
   * Zonder wie is er niemand om op te filteren. Dan de hele stapel
   * teruggeven en niet een lege lijst: een scherm dat tijdens het inladen
   * even "alles verwerkt" zegt, is een scherm dat liegt.
   */
  if (!wie) return bonnen
  return bonnen.filter((b) => vanMij(b, wie, instellingen, nu))
}
